import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useRoute, useLocation } from "wouter";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { cn } from "@/lib/utils";
import {
  Send,
  Loader2,
  MoreHorizontal,
  Pencil,
  Check,
  Heart,
  Download,
  Copy,
  RefreshCw,
  Info,
  CheckCircle2,
  ImageIcon,
  FolderOpen,
  Plus,
  ArrowLeft,
  Trash2,
  MessageSquare,
  PanelRightClose,
  PanelRightOpen,
  Minus,
} from "lucide-react";
import type { Brand, Product, Creative, CreativeGroup, CopyProject, CopyProjectCreative } from "@shared/schema";

type AdFormat = "feed" | "story" | "sidebar" | "instagram";

interface AdCard {
  id?: number;
  creativeId: number | null;
  imageUrl: string | null;
  primaryText: string;
  headline: string;
  description: string;
  ctaType: string;
  reasoning: string;
  pageName: string;
  pageLogoUrl: string | null;
  isFavorite: boolean;
  activeFormat: AdFormat;
  isEditingPrimary: boolean;
  isEditingHeadline: boolean;
  isEditingDescription: boolean;
  isEditingCta: boolean;
}

const SEGMENT_COLORS = [
  { bg: "bg-violet-100", text: "text-violet-700", border: "border-violet-200" },
  { bg: "bg-sky-100", text: "text-sky-700", border: "border-sky-200" },
  { bg: "bg-amber-100", text: "text-amber-700", border: "border-amber-200" },
  { bg: "bg-emerald-100", text: "text-emerald-700", border: "border-emerald-200" },
  { bg: "bg-rose-100", text: "text-rose-700", border: "border-rose-200" },
  { bg: "bg-cyan-100", text: "text-cyan-700", border: "border-cyan-200" },
  { bg: "bg-orange-100", text: "text-orange-700", border: "border-orange-200" },
  { bg: "bg-fuchsia-100", text: "text-fuchsia-700", border: "border-fuchsia-200" },
];

interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  type: "text" | "ads" | "generating";
  content: string;
  ads: AdCard[];
  segmentLabel?: string;
  segmentColorIndex?: number;
}

const SHORTCUT_CHIPS = [
  "Angry customer angle",
  "Clone best competitor FB ad",
  "Price vs competitor",
  "Fear of missing out",
  "Social proof hook",
  "Before & after",
  "Limited time offer",
];

function FacebookAdCard({
  card,
  textOnlyMode,
  onUpdate,
  onToggleFavorite,
  onDownload,
  onCopyText,
}: {
  card: AdCard;
  textOnlyMode: boolean;
  onUpdate: (updates: Partial<AdCard>) => void;
  onToggleFavorite: () => void;
  onDownload: (el: HTMLDivElement) => void;
  onCopyText: () => void;
}) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [expandedText, setExpandedText] = useState(false);
  const format = card.activeFormat;

  const isStory = format === "story";
  const isSidebar = format === "sidebar";
  const isInstagram = format === "instagram";

  const imageAspect = isStory ? "aspect-[9/16]" : isSidebar ? "aspect-[1.91/1]" : "aspect-square";

  return (
    <div className="flex flex-col gap-1.5">
      <div
        ref={cardRef}
        className={cn(
          "bg-white rounded-xl shadow-md overflow-hidden border border-gray-100",
          isStory && "max-w-[180px]"
        )}
        data-testid={`card-fb-ad-${card.id || "new"}`}
      >
        {!isStory && (
          <div className="flex items-center gap-2 px-3 py-2">
            {card.pageLogoUrl ? (
              <img src={card.pageLogoUrl} alt="" className="h-6 w-6 rounded-full object-cover" />
            ) : (
              <div className="h-6 w-6 rounded-full bg-indigo-500 flex items-center justify-center text-white text-[10px] font-bold">
                {(card.pageName || "A").charAt(0).toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-gray-900 truncate">{card.pageName || "Page"}</p>
              <p className="text-[10px] text-gray-500">{isInstagram ? "Sponsored" : "Sponsored · 🌐"}</p>
            </div>
            <MoreHorizontal className="h-4 w-4 text-gray-400 flex-shrink-0" />
          </div>
        )}

        {!isSidebar && (
          <div className="px-2.5 pb-1.5 relative group/text">
            {card.isEditingPrimary ? (
              <div className="relative">
                <div
                  contentEditable
                  suppressContentEditableWarning
                  className="text-xs text-gray-800 outline-none border border-blue-300 rounded p-1 min-h-[32px]"
                  onBlur={(e) => onUpdate({ primaryText: e.currentTarget.textContent || "", isEditingPrimary: false })}
                  data-testid="input-primary-text"
                >
                  {card.primaryText}
                </div>
                <button
                  className="absolute top-1 right-1 h-5 w-5 rounded bg-blue-500 text-white flex items-center justify-center"
                  onClick={() => onUpdate({ isEditingPrimary: false })}
                >
                  <Check className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <p className="text-xs text-gray-800 leading-snug">
                  {!expandedText && card.primaryText.length > 80
                    ? card.primaryText.slice(0, 80) + "..."
                    : card.primaryText}
                </p>
                {card.primaryText.length > 80 && !expandedText && (
                  <button
                    className="text-[10px] text-gray-500 font-medium hover:underline"
                    onClick={() => setExpandedText(true)}
                  >
                    See more
                  </button>
                )}
                <button
                  className="absolute top-0 right-0 h-5 w-5 rounded bg-gray-200 text-gray-600 items-center justify-center hidden group-hover/text:flex"
                  onClick={() => onUpdate({ isEditingPrimary: true })}
                  data-testid="button-edit-primary"
                >
                  <Pencil className="h-3 w-3" />
                </button>
              </div>
            )}
          </div>
        )}

        {isSidebar ? (
          <div className="flex">
            <div className={cn("w-1/2", imageAspect)}>
              {card.imageUrl && !textOnlyMode ? (
                <img src={card.imageUrl} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                  <span className="text-xs text-gray-400">Ad Visual Placeholder</span>
                </div>
              )}
            </div>
            <div className="w-1/2 bg-gray-50 p-3 flex flex-col justify-center gap-1">
              <div className="relative group/hl">
                {card.isEditingHeadline ? (
                  <div
                    contentEditable
                    suppressContentEditableWarning
                    className="text-sm font-bold text-gray-900 outline-none border border-blue-300 rounded p-0.5"
                    onBlur={(e) => onUpdate({ headline: e.currentTarget.textContent || "", isEditingHeadline: false })}
                  >
                    {card.headline}
                  </div>
                ) : (
                  <p className="text-sm font-bold text-gray-900">
                    {card.headline}
                    <button
                      className="ml-1 h-4 w-4 rounded bg-gray-200 text-gray-600 items-center justify-center hidden group-hover/hl:inline-flex align-middle"
                      onClick={() => onUpdate({ isEditingHeadline: true })}
                    >
                      <Pencil className="h-2.5 w-2.5" />
                    </button>
                  </p>
                )}
              </div>
              <p className="text-xs text-gray-500">{card.description}</p>
              <button
                className="mt-1 text-xs bg-indigo-500 text-white rounded px-2 py-1 self-start font-medium"
                onClick={() => onUpdate({ isEditingCta: !card.isEditingCta })}
              >
                {card.isEditingCta ? (
                  <input
                    className="bg-transparent text-white outline-none w-20 text-xs"
                    defaultValue={card.ctaType}
                    autoFocus
                    onBlur={(e) => onUpdate({ ctaType: e.target.value, isEditingCta: false })}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        onUpdate({ ctaType: (e.target as HTMLInputElement).value, isEditingCta: false });
                      }
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : (
                  card.ctaType
                )}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className={cn(imageAspect, "relative")}>
              {card.imageUrl && !textOnlyMode ? (
                <img src={card.imageUrl} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                  <div className="text-center">
                    <ImageIcon className="h-8 w-8 text-gray-400 mx-auto mb-1" />
                    <span className="text-sm text-gray-400">Ad Visual Placeholder</span>
                  </div>
                </div>
              )}
              {isStory && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3 pt-8">
                  <p className="text-white text-xs font-medium truncate">{card.headline}</p>
                  <button className="mt-1 text-[10px] bg-white text-black rounded-full px-3 py-0.5 font-medium">
                    {card.ctaType}
                  </button>
                </div>
              )}
            </div>

            {!isStory && (
              <div className="bg-gray-50 px-2.5 py-1.5 flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <div className="relative group/hl">
                    {card.isEditingHeadline ? (
                      <div
                        contentEditable
                        suppressContentEditableWarning
                        className="text-xs font-bold text-gray-900 outline-none border border-blue-300 rounded p-0.5"
                        onBlur={(e) => onUpdate({ headline: e.currentTarget.textContent || "", isEditingHeadline: false })}
                      >
                        {card.headline}
                      </div>
                    ) : (
                      <p className="text-xs font-bold text-gray-900 truncate">
                        {card.headline}
                        <button
                          className="ml-1 h-3.5 w-3.5 rounded bg-gray-200 text-gray-600 items-center justify-center hidden group-hover/hl:inline-flex align-middle"
                          onClick={() => onUpdate({ isEditingHeadline: true })}
                        >
                          <Pencil className="h-2 w-2" />
                        </button>
                      </p>
                    )}
                  </div>
                  <div className="relative group/desc">
                    {card.isEditingDescription ? (
                      <div
                        contentEditable
                        suppressContentEditableWarning
                        className="text-[10px] text-gray-500 outline-none border border-blue-300 rounded p-0.5"
                        onBlur={(e) => onUpdate({ description: e.currentTarget.textContent || "", isEditingDescription: false })}
                      >
                        {card.description}
                      </div>
                    ) : (
                      <p className="text-[10px] text-gray-500 truncate">
                        {card.description}
                        <button
                          className="ml-1 h-3 w-3 rounded bg-gray-200 text-gray-600 items-center justify-center hidden group-hover/desc:inline-flex align-middle"
                          onClick={() => onUpdate({ isEditingDescription: true })}
                        >
                          <Pencil className="h-2 w-2" />
                        </button>
                      </p>
                    )}
                  </div>
                </div>
                <button
                  className="text-[10px] bg-indigo-500 text-white rounded px-2.5 py-1 font-medium flex-shrink-0 hover:bg-indigo-600"
                  onClick={() => onUpdate({ isEditingCta: !card.isEditingCta })}
                >
                  {card.isEditingCta ? (
                    <input
                      className="bg-transparent text-white outline-none w-20 text-xs"
                      defaultValue={card.ctaType}
                      autoFocus
                      onBlur={(e) => onUpdate({ ctaType: e.target.value, isEditingCta: false })}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          onUpdate({ ctaType: (e.target as HTMLInputElement).value, isEditingCta: false });
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    card.ctaType
                  )}
                </button>
              </div>
            )}

            {isInstagram && (
              <div className="px-2.5 py-1.5 flex items-center gap-3 border-t border-gray-100">
                <Heart className="h-4 w-4 text-gray-600" />
                <svg className="h-5 w-5 text-gray-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
                <Send className="h-5 w-5 text-gray-600" />
              </div>
            )}
          </>
        )}
      </div>

      <div className="flex items-center gap-1 flex-wrap">
        {(["feed", "story", "sidebar", "instagram"] as AdFormat[]).map((f) => (
          <button
            key={f}
            className={cn(
              "text-[9px] px-1.5 py-0.5 rounded-full font-medium transition-colors",
              card.activeFormat === f
                ? "bg-indigo-100 text-indigo-700"
                : "bg-gray-100 text-gray-500 hover:bg-gray-200"
            )}
            onClick={() => onUpdate({ activeFormat: f })}
            data-testid={`button-format-${f}`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
        <div className="flex items-center gap-0.5 ml-auto">
          <button
            className={cn(
              "h-5 w-5 rounded flex items-center justify-center transition-colors",
              card.isFavorite ? "bg-indigo-100 text-indigo-600" : "text-gray-400 hover:text-gray-600"
            )}
            onClick={onToggleFavorite}
            data-testid="button-favorite-ad"
          >
            <Heart className={cn("h-3 w-3", card.isFavorite && "fill-current")} />
          </button>
          <button
            className="h-5 w-5 rounded text-gray-400 hover:text-gray-600 flex items-center justify-center"
            onClick={() => cardRef.current && onDownload(cardRef.current)}
            data-testid="button-download-ad"
          >
            <Download className="h-3 w-3" />
          </button>
          <button
            className="h-5 w-5 rounded text-gray-400 hover:text-gray-600 flex items-center justify-center"
            onClick={onCopyText}
            data-testid="button-copy-ad"
          >
            <Copy className="h-3 w-3" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdCopy() {
  const [, routeParams] = useRoute("/ad-copy/project/:projectId");
  const projectId = routeParams?.projectId ? Number(routeParams.projectId) : null;

  if (projectId) {
    return <AdCopyChat projectId={projectId} />;
  }
  return <AdCopyProjectList />;
}

function AdCopyProjectList() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();

  const params = new URLSearchParams(window.location.search);
  const addCreativeIdsParam = params.get("addCreatives");
  const incomingBrandId = params.get("brandId") ? Number(params.get("brandId")) : null;
  const incomingGroupName = params.get("groupName") ? decodeURIComponent(params.get("groupName")!) : "Untitled";
  const pendingCreativeIds = addCreativeIdsParam ? addCreativeIdsParam.split(",").map(Number).filter(Boolean) : [];
  const hasPendingCreatives = pendingCreativeIds.length > 0;

  const [showPicker, setShowPicker] = useState(hasPendingCreatives);
  const [isAdding, setIsAdding] = useState(false);

  const { data: projects = [], isLoading } = useQuery<CopyProject[]>({
    queryKey: ["/api/copy-projects"],
  });

  const { data: allCreatives = [] } = useQuery<Creative[]>({
    queryKey: ["/api/creatives"],
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/copy-projects/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/copy-projects"] });
      toast({ title: "Project deleted" });
    },
  });

  const getProjectCover = (projectId: number, links: CopyProjectCreative[]) => {
    const first = links.find(l => l.projectId === projectId);
    if (!first) return null;
    const creative = allCreatives.find(c => c.id === first.creativeId);
    return creative?.imageUrl || null;
  };

  const { data: allLinks = [] } = useQuery<CopyProjectCreative[]>({
    queryKey: ["/api/copy-projects/all-links"],
    queryFn: async () => {
      const results: CopyProjectCreative[] = [];
      for (const p of projects) {
        const res = await fetch(`/api/copy-projects/${p.id}/creatives`);
        const links: CopyProjectCreative[] = await res.json();
        results.push(...links);
      }
      return results;
    },
    enabled: projects.length > 0,
  });

  const addToExistingProject = async (projectId: number) => {
    setIsAdding(true);
    try {
      await apiRequest("POST", `/api/copy-projects/${projectId}/creatives`, { creativeIds: pendingCreativeIds });
      queryClient.invalidateQueries({ queryKey: ["/api/copy-projects"] });
      queryClient.invalidateQueries({ queryKey: ["/api/copy-projects/all-links"] });
      toast({ title: `Added ${pendingCreativeIds.length} creative${pendingCreativeIds.length > 1 ? "s" : ""} to project` });
      navigate(`/ad-copy/project/${projectId}`);
    } catch {
      toast({ title: "Failed to add creatives", variant: "destructive" });
    }
    setIsAdding(false);
  };

  const createNewProject = async () => {
    setIsAdding(true);
    try {
      const res = await fetch("/api/copy-projects", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: incomingGroupName,
          brandId: incomingBrandId,
          creativeIds: pendingCreativeIds,
        }),
      });
      const project = await res.json();
      queryClient.invalidateQueries({ queryKey: ["/api/copy-projects"] });
      navigate(`/ad-copy/project/${project.id}`);
    } catch {
      toast({ title: "Failed to create project", variant: "destructive" });
    }
    setIsAdding(false);
  };

  const pendingCreatives = allCreatives.filter(c => pendingCreativeIds.includes(c.id));

  return (
    <Layout>
      <div className="flex flex-col gap-6 p-6">
        <div>
          <h1 data-testid="text-page-title" className="text-2xl font-bold text-gray-900 tracking-tight">Ad Copy</h1>
          <p className="text-gray-500 mt-1">Select a project to write ad copy for your creatives.</p>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-60 rounded-xl bg-gray-100 animate-pulse" />
            ))}
          </div>
        ) : projects.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="h-20 w-20 bg-indigo-50 rounded-2xl flex items-center justify-center mb-4">
              <MessageSquare className="h-10 w-10 text-indigo-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">No copy projects yet</h3>
            <p className="text-sm text-gray-500 max-w-md">
              Go to Create Ads, select some creatives, and click "Write Ad Copy" to create your first copy project.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {projects.map((project) => {
              const projectLinks = allLinks.filter(l => l.projectId === project.id);
              const cover = getProjectCover(project.id, projectLinks);
              return (
                <div
                  key={project.id}
                  data-testid={`card-copy-project-${project.id}`}
                  className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-all cursor-pointer group relative"
                  onClick={() => navigate(`/ad-copy/project/${project.id}`)}
                >
                  <div className="h-40 bg-gray-100 relative overflow-hidden">
                    {cover ? (
                      <img src={cover} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <FolderOpen className="h-12 w-12 text-gray-300" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="secondary" size="icon" className="h-7 w-7 bg-white/90 hover:bg-white shadow-sm">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            className="text-red-600"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (confirm(`Delete "${project.name}"?`)) deleteMutation.mutate(project.id);
                            }}
                          >
                            <Trash2 className="h-4 w-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                  <div className="p-3">
                    <h3 className="font-semibold text-sm text-gray-900 truncate">{project.name}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-gray-500">
                        <ImageIcon className="h-3 w-3 inline mr-0.5" />
                        {projectLinks.length} creative{projectLinks.length !== 1 ? "s" : ""}
                      </span>
                      <span className="text-[10px] text-gray-400">
                        {new Date(project.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={showPicker} onOpenChange={(open) => { if (!open) { setShowPicker(false); navigate("/ad-copy"); } }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add {pendingCreativeIds.length} creative{pendingCreativeIds.length > 1 ? "s" : ""} to project</DialogTitle>
          </DialogHeader>

          {pendingCreatives.length > 0 && (
            <div className="flex gap-2 overflow-x-auto pb-2">
              {pendingCreatives.slice(0, 6).map(c => (
                <img key={c.id} src={c.imageUrl!} alt="" className="h-16 w-16 rounded-lg object-cover border border-gray-200 flex-shrink-0" />
              ))}
              {pendingCreatives.length > 6 && (
                <div className="h-16 w-16 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 text-xs text-gray-500 font-medium">
                  +{pendingCreatives.length - 6}
                </div>
              )}
            </div>
          )}

          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            <button
              className="w-full flex items-center gap-3 p-3 rounded-xl border-2 border-dashed border-indigo-300 bg-indigo-50/50 hover:bg-indigo-50 transition-colors text-left disabled:opacity-50"
              onClick={createNewProject}
              disabled={isAdding}
              data-testid="button-create-new-project"
            >
              <div className="h-10 w-10 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <Plus className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-indigo-700">Create new project</p>
                <p className="text-xs text-indigo-500">"{incomingGroupName}"</p>
              </div>
              {isAdding && <Loader2 className="h-4 w-4 animate-spin ml-auto text-indigo-500" />}
            </button>

            {projects.map((project) => {
              const projectLinks = allLinks.filter(l => l.projectId === project.id);
              const cover = getProjectCover(project.id, projectLinks);
              return (
                <button
                  key={project.id}
                  className="w-full flex items-center gap-3 p-3 rounded-xl border border-gray-200 hover:border-indigo-300 hover:bg-indigo-50/30 transition-colors text-left disabled:opacity-50"
                  onClick={() => addToExistingProject(project.id)}
                  disabled={isAdding}
                  data-testid={`button-add-to-project-${project.id}`}
                >
                  <div className="h-10 w-10 rounded-lg bg-gray-100 overflow-hidden flex-shrink-0">
                    {cover ? (
                      <img src={cover} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <FolderOpen className="h-5 w-5 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{project.name}</p>
                    <p className="text-xs text-gray-500">{projectLinks.length} creative{projectLinks.length !== 1 ? "s" : ""}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function AdCopyChat({ projectId }: { projectId: number }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();
  const [selectedBrandId, setSelectedBrandId] = useState<number | null>(null);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [selectedCreativeIds, setSelectedCreativeIds] = useState<number[]>([]);
  const [textOnlyMode, setTextOnlyMode] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [quantity, setQuantity] = useState(3);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sessionId, setSessionId] = useState<number | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { data: project } = useQuery<CopyProject>({
    queryKey: ["/api/copy-projects", projectId],
    queryFn: async () => {
      const res = await fetch(`/api/copy-projects/${projectId}`);
      if (!res.ok) throw new Error("Not found");
      return res.json();
    },
  });

  const { data: projectLinks = [] } = useQuery<CopyProjectCreative[]>({
    queryKey: ["/api/copy-projects", projectId, "creatives"],
    queryFn: async () => {
      const res = await fetch(`/api/copy-projects/${projectId}/creatives`);
      return res.json();
    },
  });

  const { data: brands = [] } = useQuery<Brand[]>({
    queryKey: ["/api/brands"],
  });

  const { data: products = [] } = useQuery<Product[]>({
    queryKey: ["/api/brands", selectedBrandId, "products"],
    queryFn: async () => {
      if (!selectedBrandId) return [];
      const res = await fetch(`/api/brands/${selectedBrandId}/products`);
      return res.json();
    },
    enabled: !!selectedBrandId,
  });

  const { data: allCreatives = [], isLoading: creativesLoading } = useQuery<Creative[]>({
    queryKey: ["/api/creatives"],
    queryFn: async () => {
      const res = await fetch("/api/creatives");
      return res.json();
    },
  });

  const { data: creativeGroups = [] } = useQuery<CreativeGroup[]>({
    queryKey: ["/api/creative-groups"],
    queryFn: async () => {
      const res = await fetch("/api/creative-groups");
      return res.json();
    },
  });

  const projectCreativeIds = new Set(projectLinks.map(l => l.creativeId));
  const projectCreatives = allCreatives.filter(c => projectCreativeIds.has(c.id) && c.imageUrl && c.status === "completed");

  useEffect(() => {
    if (selectedBrandId) return;
    if (project?.brandId) {
      setSelectedBrandId(project.brandId);
    } else if (projectCreatives.length > 0) {
      const firstBrandId = projectCreatives.find(c => c.brandId)?.brandId;
      if (firstBrandId) setSelectedBrandId(firstBrandId);
    }
  }, [project, projectCreatives]);

  const [historyLoaded, setHistoryLoaded] = useState(false);
  useEffect(() => {
    if (historyLoaded) return;
    const loadHistory = async () => {
      try {
        const res = await fetch(`/api/copy-projects/${projectId}/history`);
        if (!res.ok) return;
        const data = await res.json();
        if (data.sessionId) {
          setSessionId(data.sessionId);
        }
        if (data.messages && data.messages.length > 0) {
          const loaded: ChatMessage[] = data.messages.map((msg: any, idx: number) => {
            if (msg.role === "user") {
              return { id: `hist-${idx}`, role: "user" as const, type: "text" as const, content: msg.content, ads: [] };
            }
            const adCards: AdCard[] = (msg.ads || []).map((a: any) => ({
              id: a.id,
              creativeId: a.creativeId,
              imageUrl: a.imageUrl,
              primaryText: a.primaryText || "",
              headline: a.headline || "",
              description: a.description || "",
              ctaType: a.ctaType || "Learn More",
              reasoning: a.reasoning || "",
              pageName: a.pageName || "",
              pageLogoUrl: a.pageLogoUrl || null,
              isFavorite: a.isFavorite || false,
              activeFormat: "feed" as AdFormat,
              isEditingPrimary: false,
              isEditingHeadline: false,
              isEditingDescription: false,
              isEditingCta: false,
            }));
            return {
              id: `hist-${idx}`,
              role: "assistant" as const,
              type: adCards.length > 0 ? "ads" as const : "text" as const,
              content: msg.content || "",
              ads: adCards,
            };
          });
          setMessages(loaded);
        }
      } catch {}
      setHistoryLoaded(true);
    };
    loadHistory();
  }, [projectId, historyLoaded]);

  useEffect(() => {
    if (projectLinks.length > 0 && selectedCreativeIds.length === 0) {
      setSelectedCreativeIds(projectLinks.map(l => l.creativeId));
    }
  }, [projectLinks]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 100) + "px";
    }
  }, [inputValue]);

  const addCreativeMutation = useMutation({
    mutationFn: async (creativeIds: number[]) => {
      await apiRequest("POST", `/api/copy-projects/${projectId}/creatives`, { creativeIds });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/copy-projects", projectId, "creatives"] });
    },
  });

  const removeCreativeMutation = useMutation({
    mutationFn: async (linkId: number) => {
      await apiRequest("DELETE", `/api/copy-project-creatives/${linkId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/copy-projects", projectId, "creatives"] });
      toast({ title: "Creative removed from project" });
    },
  });

  const toggleCreativeSelection = (id: number) => {
    setSelectedCreativeIds((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
    );
  };

  const downloadCardAsPng = async (el: HTMLDivElement) => {
    try {
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(el, { useCORS: true, allowTaint: true });
      const link = document.createElement("a");
      link.download = `ad-preview-${Date.now()}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch {
      toast({ title: "Download failed", variant: "destructive" });
    }
  };

  const downloadAllCards = async (ads: AdCard[]) => {
    for (const ad of ads) {
      const el = document.querySelector(`[data-testid="card-fb-ad-${ad.id || 'new'}"]`) as HTMLDivElement;
      if (el) await downloadCardAsPng(el);
    }
  };

  const copyAdText = (ad: AdCard) => {
    const text = `${ad.primaryText}\n\n${ad.headline}\n${ad.description}\n\nCTA: ${ad.ctaType}`;
    navigator.clipboard.writeText(text);
    toast({ title: "Copied to clipboard" });
  };

  const updateAdInMessage = (messageId: string, adIndex: number, updates: Partial<AdCard>) => {
    setMessages((prev) =>
      prev.map((msg) => {
        if (msg.id !== messageId) return msg;
        const newAds = [...msg.ads];
        newAds[adIndex] = { ...newAds[adIndex], ...updates };
        return { ...msg, ads: newAds };
      })
    );

    const msg = messages.find(m => m.id === messageId);
    if (msg && msg.ads[adIndex]?.id) {
      const ad = msg.ads[adIndex];
      const saveUpdates: Partial<Pick<AdCard, "primaryText" | "headline" | "description" | "ctaType">> = {};
      if (updates.primaryText !== undefined) saveUpdates.primaryText = updates.primaryText;
      if (updates.headline !== undefined) saveUpdates.headline = updates.headline;
      if (updates.description !== undefined) saveUpdates.description = updates.description;
      if (updates.ctaType !== undefined) saveUpdates.ctaType = updates.ctaType;
      if (Object.keys(saveUpdates).length > 0) {
        apiRequest("PATCH", `/api/copy-ads/${ad.id}`, saveUpdates).catch(() => {});
      }
    }
  };

  const toggleFavorite = async (messageId: string, adIndex: number) => {
    const msg = messages.find(m => m.id === messageId);
    if (!msg || !msg.ads[adIndex]?.id) return;
    const ad = msg.ads[adIndex];
    try {
      await apiRequest("PATCH", `/api/copy-ads/${ad.id}/favorite`);
      updateAdInMessage(messageId, adIndex, { isFavorite: !ad.isFavorite });
    } catch {}
  };

  const parseApiAds = (ads: any[]): AdCard[] => {
    return ads.map((a: any) => ({
      id: a.id,
      creativeId: a.creativeId,
      imageUrl: a.imageUrl,
      primaryText: a.primaryText || "",
      headline: a.headline || "",
      description: a.description || "",
      ctaType: a.ctaType || "Learn More",
      reasoning: a.reasoning || "",
      pageName: a.pageName || "",
      pageLogoUrl: a.pageLogoUrl || null,
      isFavorite: a.isFavorite || false,
      activeFormat: "feed" as AdFormat,
      isEditingPrimary: false,
      isEditingHeadline: false,
      isEditingDescription: false,
      isEditingCta: false,
    }));
  };

  const sendSingleSegment = async (segmentText: string, segmentLabel: string | undefined, segmentColorIndex: number | undefined, currentSessionId: number | null): Promise<number | null> => {
    const genMsgId = crypto.randomUUID();

    setMessages((prev) => [
      ...prev,
      { id: genMsgId, role: "assistant", type: "generating", content: "", ads: [], segmentLabel, segmentColorIndex },
    ]);

    try {
      const res = await apiRequest("POST", "/api/copy/chat", {
        brandId: selectedBrandId,
        productId: selectedProductId,
        projectId,
        creativeIds: textOnlyMode ? [] : selectedCreativeIds,
        textOnlyMode,
        message: segmentText,
        quantity,
        conversationHistory: [],
        sessionId: currentSessionId,
      });

      const data = await res.json();
      const newSessionId = data.sessionId || currentSessionId;

      if (data.type === "ads" && data.ads) {
        const adCards = parseApiAds(data.ads);
        setMessages((prev) =>
          prev.map((m) =>
            m.id === genMsgId
              ? { ...m, type: "ads", content: data.message || "", ads: adCards }
              : m
          )
        );
      } else {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === genMsgId
              ? { ...m, type: "text", content: data.message || "I couldn't generate ads. Please try again." }
              : m
          )
        );
      }

      return newSessionId;
    } catch {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === genMsgId
            ? { ...m, type: "text", content: "Something went wrong. Please try again." }
            : m
        )
      );
      return currentSessionId;
    }
  };

  const sendMessage = async (text?: string) => {
    const msg = text || inputValue.trim();
    if (!msg || isGenerating || !selectedBrandId) return;

    setInputValue("");

    const segments = msg.split("|").map(s => s.trim()).filter(Boolean);
    const isMultiSegment = segments.length > 1;

    const userMsgId = crypto.randomUUID();
    setMessages((prev) => [
      ...prev,
      { id: userMsgId, role: "user", type: "text", content: msg, ads: [] },
    ]);

    setIsGenerating(true);

    let currentSessionId = sessionId;

    for (let i = 0; i < segments.length; i++) {
      const segmentLabel = isMultiSegment ? segments[i] : undefined;
      const segmentColorIndex = isMultiSegment ? i % SEGMENT_COLORS.length : undefined;

      const newSessionId = await sendSingleSegment(
        segments[i],
        segmentLabel,
        segmentColorIndex,
        currentSessionId
      );

      if (newSessionId && !currentSessionId) {
        currentSessionId = newSessionId;
        setSessionId(newSessionId);
      }
    }

    setIsGenerating(false);
  };

  const resendLastPrompt = () => {
    const lastUserMsg = [...messages].reverse().find(m => m.role === "user");
    if (lastUserMsg) sendMessage(lastUserMsg.content);
  };

  return (
    <Layout>
      <div className="h-[calc(100vh-1px)] flex" data-testid="page-ad-copy">
        <div className="flex-1 flex flex-col min-w-0" style={{ flex: sidebarOpen ? "0 0 75%" : "1 1 100%" }}>
          <div className="flex items-center gap-2 px-4 py-1.5 border-b border-gray-200 bg-white flex-shrink-0">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => navigate("/ad-copy")}
              data-testid="button-back"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-sm font-bold text-gray-900" data-testid="text-page-title">{project?.name || "Ad Copy"}</h1>
            <Select
              value={selectedBrandId?.toString() || ""}
              onValueChange={(v) => {
                setSelectedBrandId(Number(v));
                setSelectedProductId(null);
              }}
            >
              <SelectTrigger className="w-48 h-9" data-testid="select-brand">
                <SelectValue placeholder="Select brand" />
              </SelectTrigger>
              <SelectContent>
                {brands.map((b) => (
                  <SelectItem key={b.id} value={b.id.toString()}>
                    {b.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={selectedProductId?.toString() || ""}
              onValueChange={(v) => setSelectedProductId(v ? Number(v) : null)}
              disabled={!selectedBrandId}
            >
              <SelectTrigger className="w-48 h-9" data-testid="select-product">
                <SelectValue placeholder="Select product (optional)" />
              </SelectTrigger>
              <SelectContent>
                {products.map((p) => (
                  <SelectItem key={p.id} value={p.id.toString()}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="ml-auto">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                data-testid="button-toggle-sidebar"
              >
                {sidebarOpen ? <PanelRightClose className="h-4 w-4" /> : <PanelRightOpen className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-6 space-y-4">
            {messages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <div className="h-16 w-16 rounded-2xl bg-indigo-50 flex items-center justify-center mb-4">
                  <Send className="h-8 w-8 text-indigo-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Ad Copy Generator</h3>
                <p className="text-sm text-gray-500 max-w-md">
                  Your creatives from "{project?.name}" are loaded on the right.
                  Select a brand, then ask Claude to generate Facebook ad copy.
                </p>
              </div>
            )}

            {messages.map((msg) => {
              if (msg.role === "user") {
                const hasSegments = msg.content.includes("|");
                const segments = hasSegments ? msg.content.split("|").map(s => s.trim()).filter(Boolean) : [];
                return (
                  <div key={msg.id} className="flex justify-end">
                    <div
                      className="bg-indigo-600 text-white rounded-2xl rounded-br-md px-4 py-2.5 text-sm max-w-[70%]"
                      data-testid="message-user"
                    >
                      {hasSegments ? (
                        <div className="flex flex-wrap gap-1.5">
                          {segments.map((seg, idx) => {
                            const color = SEGMENT_COLORS[idx % SEGMENT_COLORS.length];
                            return (
                              <span
                                key={idx}
                                className={cn(
                                  "inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border",
                                  color.bg, color.text, color.border
                                )}
                                data-testid={`segment-tag-${idx}`}
                              >
                                {seg}
                              </span>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                      )}
                    </div>
                  </div>
                );
              }

              if (msg.type === "generating") {
                const segColor = msg.segmentColorIndex !== undefined ? SEGMENT_COLORS[msg.segmentColorIndex] : null;
                return (
                  <div key={msg.id} className="flex justify-start">
                    <div className="bg-white rounded-2xl rounded-bl-md px-4 py-3 shadow-sm border border-gray-100">
                      <div className="flex items-center gap-2">
                        {segColor && msg.segmentLabel && (
                          <span className={cn("inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border", segColor.bg, segColor.text, segColor.border)}>
                            {msg.segmentLabel}
                          </span>
                        )}
                        <div className="flex gap-1">
                          <div className="h-2 w-2 rounded-full bg-indigo-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                          <div className="h-2 w-2 rounded-full bg-indigo-400 animate-bounce" style={{ animationDelay: "150ms" }} />
                          <div className="h-2 w-2 rounded-full bg-indigo-400 animate-bounce" style={{ animationDelay: "300ms" }} />
                        </div>
                        <span className="text-xs text-gray-500">Generating ad copy...</span>
                      </div>
                    </div>
                  </div>
                );
              }

              if (msg.type === "ads" && msg.ads.length > 0) {
                const segColor = msg.segmentColorIndex !== undefined ? SEGMENT_COLORS[msg.segmentColorIndex] : null;
                return (
                  <div key={msg.id} className="w-full space-y-3">
                    <div className="flex items-center gap-2">
                      {segColor && msg.segmentLabel && (
                        <span className={cn("inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border", segColor.bg, segColor.text, segColor.border)}>
                          {msg.segmentLabel}
                        </span>
                      )}
                      <p className="text-xs text-gray-500 font-medium">
                        Claude generated {msg.ads.length} ad variation{msg.ads.length > 1 ? "s" : ""}
                      </p>
                    </div>
                    {msg.content && (
                      <p className="text-sm text-gray-600 bg-white rounded-lg px-3 py-2 border border-gray-100">{msg.content}</p>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                      {msg.ads.map((ad, idx) => (
                        <FacebookAdCard
                          key={idx}
                          card={ad}
                          textOnlyMode={textOnlyMode && !ad.imageUrl}
                          onUpdate={(updates) => updateAdInMessage(msg.id, idx, updates)}
                          onToggleFavorite={() => toggleFavorite(msg.id, idx)}
                          onDownload={downloadCardAsPng}
                          onCopyText={() => copyAdText(ad)}
                        />
                      ))}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs"
                        onClick={resendLastPrompt}
                        disabled={isGenerating}
                        data-testid="button-regenerate"
                      >
                        <RefreshCw className="h-3 w-3 mr-1" />
                        Regenerate
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs"
                        onClick={() => downloadAllCards(msg.ads)}
                        data-testid="button-download-all"
                      >
                        <Download className="h-3 w-3 mr-1" />
                        Download All
                      </Button>
                      <span className="text-[10px] text-gray-400 bg-gray-100 rounded-full px-2 py-0.5">
                        {msg.ads.length} ads
                      </span>
                    </div>
                  </div>
                );
              }

              return (
                <div key={msg.id} className="flex justify-start">
                  <div
                    className="bg-white rounded-2xl rounded-bl-md px-4 py-2.5 text-sm text-gray-700 max-w-[70%] shadow-sm border border-gray-100"
                    data-testid="message-assistant"
                  >
                    <p className="whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="border-t border-gray-200 bg-white px-6 py-3 flex-shrink-0">
            <div className="flex gap-2 mb-2 overflow-x-auto pb-1">
              {SHORTCUT_CHIPS.map((chip) => (
                <button
                  key={chip}
                  className="text-xs px-3 py-1.5 rounded-full bg-gray-100 text-gray-600 hover:bg-indigo-50 hover:text-indigo-600 whitespace-nowrap transition-colors flex-shrink-0"
                  onClick={() => {
                    setInputValue(prev => {
                      const trimmed = prev.trim();
                      if (!trimmed) return chip;
                      if (trimmed.endsWith("|")) return trimmed + " " + chip;
                      return trimmed + " | " + chip;
                    });
                    textareaRef.current?.focus();
                  }}
                  data-testid={`chip-${chip.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {chip}
                </button>
              ))}
            </div>
            {(() => {
              const inputSegments = inputValue.split("|").map(s => s.trim()).filter(Boolean);
              const hasCompletedSegments = inputValue.includes("|") && inputSegments.length > 0;
              const lastPart = inputValue.includes("|") ? inputValue.split("|").pop() || "" : inputValue;
              const completedSegments = hasCompletedSegments ? inputValue.split("|").slice(0, -1).map(s => s.trim()).filter(Boolean) : [];

              return (
                <div className="flex items-end gap-2">
                  <div className="flex items-center gap-1 bg-gray-100 rounded-lg px-1.5 py-1 flex-shrink-0" data-testid="quantity-selector">
                    <button
                      className="h-6 w-6 rounded flex items-center justify-center text-gray-500 hover:bg-gray-200 transition-colors disabled:opacity-40"
                      onClick={() => setQuantity(q => Math.max(1, q - 1))}
                      disabled={quantity <= 1}
                      data-testid="button-quantity-minus"
                    >
                      <Minus className="h-3 w-3" />
                    </button>
                    <span className="text-xs font-semibold text-gray-700 w-5 text-center" data-testid="text-quantity">{quantity}</span>
                    <button
                      className="h-6 w-6 rounded flex items-center justify-center text-gray-500 hover:bg-gray-200 transition-colors disabled:opacity-40"
                      onClick={() => setQuantity(q => Math.min(10, q + 1))}
                      disabled={quantity >= 10}
                      data-testid="button-quantity-plus"
                    >
                      <Plus className="h-3 w-3" />
                    </button>
                    <span className="text-[10px] text-gray-400 ml-0.5">ads{!textOnlyMode && selectedCreativeIds.length > 1 ? `/cr` : ""}</span>
                  </div>
                  <div
                    className="flex-1 flex flex-wrap items-center gap-1.5 border border-gray-200 rounded-xl px-3 py-2 bg-gray-50/50 focus-within:ring-2 focus-within:ring-indigo-500 focus-within:border-transparent min-h-[42px] cursor-text"
                    onClick={() => textareaRef.current?.focus()}
                  >
                    {completedSegments.map((seg, idx) => {
                      const color = SEGMENT_COLORS[idx % SEGMENT_COLORS.length];
                      return (
                        <span
                          key={idx}
                          className={cn(
                            "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border",
                            color.bg, color.text, color.border
                          )}
                          data-testid={`input-segment-${idx}`}
                        >
                          {seg}
                          <button
                            className="h-3.5 w-3.5 rounded-full hover:bg-black/10 flex items-center justify-center"
                            onClick={(e) => {
                              e.stopPropagation();
                              const parts = inputValue.split("|");
                              parts.splice(idx, 1);
                              setInputValue(parts.join("|").trim());
                            }}
                            data-testid={`button-remove-segment-${idx}`}
                          >
                            ×
                          </button>
                        </span>
                      );
                    })}
                    <textarea
                      ref={textareaRef}
                      className="flex-1 min-w-[120px] resize-none bg-transparent text-sm focus:outline-none"
                      placeholder={completedSegments.length > 0 ? "Add another angle..." : `Type your prompt... use | to separate angles`}
                      value={lastPart}
                      onChange={(e) => {
                        if (completedSegments.length > 0) {
                          setInputValue(completedSegments.join(" | ") + " | " + e.target.value);
                        } else {
                          setInputValue(e.target.value);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          sendMessage();
                        }
                        if (e.key === "Backspace" && lastPart === "" && completedSegments.length > 0) {
                          e.preventDefault();
                          const parts = inputValue.split("|").filter(s => s.trim());
                          parts.pop();
                          setInputValue(parts.length > 0 ? parts.join(" | ") + " | " : "");
                        }
                      }}
                      rows={1}
                      style={{ maxHeight: 60 }}
                      disabled={isGenerating || !selectedBrandId}
                      data-testid="input-chat-message"
                    />
                  </div>
                  <Button
                    className={cn(
                      "h-10 w-10 rounded-xl flex-shrink-0",
                      inputValue.trim() && !isGenerating && selectedBrandId
                        ? "bg-indigo-600 hover:bg-indigo-700 text-white"
                        : "bg-gray-100 text-gray-400 cursor-not-allowed"
                    )}
                    onClick={() => sendMessage()}
                    disabled={!inputValue.trim() || isGenerating || !selectedBrandId}
                    data-testid="button-send"
                  >
                    {isGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </Button>
                </div>
              );
            })()}
            {!selectedBrandId && (
              <p className="text-xs text-amber-600 mt-1">Select a brand to start generating ad copy</p>
            )}
          </div>
        </div>

        {sidebarOpen && <div className="border-l border-gray-200 bg-white flex flex-col" style={{ flex: "0 0 25%" }}>
          <div className="flex items-center justify-between px-3 py-1.5 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-gray-900">Project Creatives</h2>
              {selectedCreativeIds.length > 0 && (
                <span className="text-xs bg-indigo-100 text-indigo-700 rounded-full px-2 py-0.5" data-testid="text-selected-count">
                  {selectedCreativeIds.length} selected
                </span>
              )}
            </div>
            <Tooltip>
              <TooltipTrigger>
                <Info className="h-4 w-4 text-gray-400" />
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs max-w-[200px]">Only creatives in this project are shown. Claude uses selected visuals to write contextual copy.</p>
              </TooltipContent>
            </Tooltip>
          </div>

          <div className="flex-1 overflow-y-auto p-3">
            {creativesLoading ? (
              <div className="grid grid-cols-2 gap-2">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="aspect-square rounded-lg bg-gray-100 animate-pulse" />
                ))}
              </div>
            ) : projectCreatives.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center px-6">
                <ImageIcon className="h-10 w-10 text-gray-300 mb-3" />
                <p className="text-sm text-gray-500">No creatives in this project yet.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {projectCreatives.map((creative) => {
                  const isSelected = selectedCreativeIds.includes(creative.id);
                  const group = creativeGroups.find(g => g.id === creative.groupId);
                  const link = projectLinks.find(l => l.creativeId === creative.id);
                  return (
                    <div
                      key={creative.id}
                      className={cn(
                        "relative rounded-lg overflow-hidden border-2 transition-all aspect-square group/card cursor-pointer",
                        isSelected
                          ? "border-indigo-500 ring-2 ring-indigo-200"
                          : "border-gray-200 hover:border-gray-300"
                      )}
                      onClick={() => toggleCreativeSelection(creative.id)}
                      data-testid={`card-creative-${creative.id}`}
                    >
                      <img
                        src={creative.imageUrl!}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                      {group && (
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-2 py-1.5">
                          <p className="text-[10px] text-white font-medium truncate">{group.name}</p>
                        </div>
                      )}
                      {isSelected && (
                        <div className="absolute top-1.5 right-1.5 h-5 w-5 rounded-full bg-indigo-500 flex items-center justify-center">
                          <Check className="h-3 w-3 text-white" />
                        </div>
                      )}
                      {link && (
                        <button
                          className="absolute top-1.5 left-1.5 h-5 w-5 rounded-full bg-red-500/80 hover:bg-red-600 flex items-center justify-center opacity-0 group-hover/card:opacity-100 transition-opacity"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeCreativeMutation.mutate(link.id);
                            setSelectedCreativeIds(prev => prev.filter(id => id !== creative.id));
                          }}
                          data-testid={`button-remove-creative-${creative.id}`}
                        >
                          <Trash2 className="h-2.5 w-2.5 text-white" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="border-t border-gray-200 px-3 py-1.5 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2">
              <Switch
                checked={textOnlyMode}
                onCheckedChange={setTextOnlyMode}
                data-testid="switch-text-only"
              />
              <Label className="text-xs text-gray-600">Text Only</Label>
            </div>
          </div>
        </div>}
      </div>
    </Layout>
  );
}
